import { useState, useMemo } from 'react';
import { X, Check } from 'lucide-react';
import {
  REQUIRED_PERMISSIONS,
  allAvailablePermissions,
  categoryConfig,
  mergeWithRequiredPermissions,
  normalizePermissionsFromApi,
  sanitizePermissionsForSave,
  ALL_PERMISSION_KEYS,
  canManagePlatformModules,
} from '../../config/permissionDefinitions';

function getCurrentUserRole() {
  try {
    const userData = localStorage.getItem('admin_user');
    if (userData) {
      const user = JSON.parse(userData);
      const role = user.role || null;
      if (role === 'super-admin' || role === 'SUPER_ADMIN') return 'SUPER_ADMIN';
      if (role === 'commercant' || role === 'COMMERCANT') return 'COMMERCANT';
      if (role === 'ADMIN') return 'ADMIN';
      return role;
    }
  } catch (error) {
    console.error('Erreur parsing user data pour rôle:', error);
  }
  return null;
}

export default function PermissionModal({
  commercant,
  permissions: activePermissions,
  onClose,
  onSave,
  initialCategory = 'produits',
  readOnly = false,
}) {
  const [selectedPermissions, setSelectedPermissions] = useState(() =>
    normalizePermissionsFromApi(activePermissions),
  );
  const [activeCategory, setActiveCategory] = useState(initialCategory);

  const currentUserRole = getCurrentUserRole();
  const isCommercant = currentUserRole === 'COMMERCANT';
  const isReadOnly = readOnly || !canManagePlatformModules(currentUserRole);

  const permissionCategories = useMemo(() => {
    return Object.keys(categoryConfig).reduce((acc, catKey) => {
      const permissionsInCategory = Object.entries(allAvailablePermissions)
        .filter(([key, perm]) => {
          if (perm.category !== catKey) return false;
          if (isCommercant && key === 'GESTION_COMMERCANTS') return false;
          return true;
        })
        .map(([key, perm]) => ({ key, ...perm }));

      if (permissionsInCategory.length > 0) {
        acc[catKey] = {
          ...categoryConfig[catKey],
          permissions: permissionsInCategory,
        };
      }
      return acc;
    }, {});
  }, [isCommercant]);

  const togglePermission = (key) => {
    if (isReadOnly) return;
    const permission = allAvailablePermissions[key];
    if (permission?.disabled) return;

    if (REQUIRED_PERMISSIONS.includes(key) && selectedPermissions.includes(key)) {
      alert('Cette permission est requise et ne peut pas être désactivée.');
      return;
    }

    if (selectedPermissions.includes(key)) {
      setSelectedPermissions(selectedPermissions.filter((p) => p !== key));
    } else {
      setSelectedPermissions([...selectedPermissions, key]);
    }
  };

  const toggleCategory = (categoryKey) => {
    if (isReadOnly) return;
    const category = permissionCategories[categoryKey];
    const activablePermissions = category.permissions.filter(
      (p) => !p.disabled && !p.future && !REQUIRED_PERMISSIONS.includes(p.key),
    );
    const allSelected =
      activablePermissions.length > 0 &&
      activablePermissions.every((p) => selectedPermissions.includes(p.key));

    if (allSelected) {
      const toRemove = activablePermissions.map((p) => p.key);
      setSelectedPermissions(
        selectedPermissions.filter(
          (p) => !toRemove.includes(p) || REQUIRED_PERMISSIONS.includes(p),
        ),
      );
    } else {
      const toAdd = activablePermissions
        .map((p) => p.key)
        .filter((key) => !selectedPermissions.includes(key));
      setSelectedPermissions([...selectedPermissions, ...toAdd]);
    }
  };

  const categoryKeys = Object.keys(permissionCategories);
  const resolvedCategory = permissionCategories[activeCategory]
    ? activeCategory
    : categoryKeys[0];
  const activeCategoryData =
    permissionCategories[resolvedCategory] || permissionCategories[categoryKeys[0]];
  const selectedCount = selectedPermissions.length;
  const totalCount = ALL_PERMISSION_KEYS.length;

  if (!activeCategoryData) {
    return null;
  }

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="relative mx-auto border w-11/12 md:w-4/5 lg:w-3/4 xl:w-2/3 shadow-xl rounded-lg bg-white max-h-[90vh] flex flex-col">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div>
            <h3 className="text-xl font-semibold text-gray-900">
              {isReadOnly ? 'Autorisations' : 'Permissions'} — {commercant.nom}
            </h3>
            <p className="text-sm text-gray-500 mt-1">
              {selectedCount} / {totalCount} permissions activées
            </p>
            {isReadOnly ? (
              <p className="text-xs text-gray-600 mt-1">
                Consultation seule. Seul Clickorico peut modifier vos modules et autorisations.
              </p>
            ) : (
              <p className="text-xs text-emerald-700 mt-1">
                Les permissions contrôlent l’accès aux modules (qui peut ouvrir Communication, la
                caisse, etc.). L’offre Communication Inclus / Plus (capacités d’envoi) se gère
                séparément sur l’onglet Communication de la fiche.
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <div className="flex flex-1 overflow-hidden min-h-0">
          <div className="w-64 border-r border-gray-200 bg-gray-50 overflow-y-auto shrink-0">
            <div className="p-4 space-y-2">
              {Object.entries(permissionCategories).map(([key, category]) => {
                const categorySelected = category.permissions.every((p) =>
                  selectedPermissions.includes(p.key),
                );
                const categoryPartial =
                  category.permissions.some((p) => selectedPermissions.includes(p.key)) &&
                  !categorySelected;
                const active = resolvedCategory === key;

                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setActiveCategory(key)}
                    className={`w-full text-left p-3 rounded-lg transition-all ${
                      active ? category.color + ' shadow-sm' : 'hover:bg-gray-100 text-gray-700'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <span className="text-xl">{category.icon}</span>
                        <span className="font-medium text-sm">{category.label}</span>
                      </div>
                      {categorySelected && <Check className="h-4 w-4 text-green-600" />}
                      {categoryPartial && (
                        <div className="h-2 w-2 rounded-full bg-orange-400" />
                      )}
                    </div>
                    <div className="mt-1 text-xs text-gray-500">
                      {category.permissions.filter((p) => selectedPermissions.includes(p.key)).length}{' '}
                      / {category.permissions.length}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="flex-1 overflow-y-auto min-h-0">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <div
                  className={`inline-flex items-center space-x-3 px-4 py-2 rounded-lg ${activeCategoryData.color}`}
                >
                  <span className="text-2xl">{activeCategoryData.icon}</span>
                  <h4 className="text-lg font-semibold">{activeCategoryData.label}</h4>
                </div>
                {!isReadOnly && (
                  <button
                    type="button"
                    onClick={() => toggleCategory(resolvedCategory)}
                    className="text-sm text-primary-600 hover:text-primary-800 font-medium"
                  >
                    {activeCategoryData.permissions
                      .filter((p) => !p.disabled && !p.future)
                      .every((p) => selectedPermissions.includes(p.key))
                      ? 'Tout désélectionner'
                      : 'Tout sélectionner'}
                  </button>
                )}
              </div>

              <div className="space-y-3">
                {activeCategoryData.permissions.map((permission) => {
                  const isSelected = selectedPermissions.includes(permission.key);
                  const isRequired =
                    permission.required || REQUIRED_PERMISSIONS.includes(permission.key);
                  const isDisabled =
                    isReadOnly ||
                    permission.disabled ||
                    permission.future ||
                    (isRequired && isSelected);
                  const isFuture = permission.future;

                  return (
                    <label
                      key={permission.key}
                      className={`flex items-start space-x-4 p-4 rounded-lg border-2 transition-all ${
                        isDisabled
                          ? 'border-gray-200 bg-gray-100 cursor-not-allowed opacity-60'
                          : isSelected
                            ? 'border-primary-300 bg-primary-50 cursor-pointer'
                            : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50 cursor-pointer'
                      }`}
                      title={isFuture && permission.message ? permission.message : ''}
                    >
                      <input
                        type="checkbox"
                        checked={isSelected}
                        disabled={isDisabled}
                        onChange={() => togglePermission(permission.key)}
                        className={`mt-1 h-5 w-5 text-primary-600 rounded focus:ring-primary-500 ${
                          isDisabled ? 'cursor-not-allowed opacity-50' : ''
                        }`}
                      />
                      <div className="flex-1">
                        <div className="flex items-center space-x-2">
                          <span className="text-xl">{permission.icon}</span>
                          <span
                            className={`font-medium ${isSelected ? 'text-primary-900' : isDisabled ? 'text-gray-400' : 'text-gray-900'}`}
                          >
                            {permission.label}
                            {isRequired && isSelected && (
                              <span className="ml-2 text-xs bg-blue-100 text-blue-800 px-2 py-0.5 rounded">
                                Requis
                              </span>
                            )}
                            {isFuture && (
                              <span className="ml-2 text-xs bg-yellow-100 text-yellow-800 px-2 py-0.5 rounded">
                                Bientôt
                              </span>
                            )}
                          </span>
                        </div>
                        <p className="text-sm text-gray-500 mt-1">{permission.description}</p>
                      </div>
                      {isSelected && !isDisabled && (
                        <Check className="h-5 w-5 text-primary-600 flex-shrink-0" />
                      )}
                    </label>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between bg-gray-50 shrink-0">
          <div className="text-sm text-gray-600">
            <span className="font-medium text-primary-600">{selectedCount}</span> permissions
            sélectionnées
          </div>
          <div className="flex space-x-3">
            <button type="button" onClick={onClose} className="btn-secondary">
              {isReadOnly ? 'Fermer' : 'Annuler'}
            </button>
            {!isReadOnly && (
              <button
                type="button"
                onClick={() => onSave(sanitizePermissionsForSave(selectedPermissions))}
                className="btn-primary"
              >
                Sauvegarder les permissions
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
